import React, { forwardRef, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AssetCategoriesSearchListQueryVariables,
  AssetCategory,
  AssetCategoryFilterInputObject,
  FilterGroupingEnum,
  useAssetCategoriesSearchListQuery,
} from 'graphql-common';
import Chip from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { FloatingLabelProps } from '@lib/hocs/withFloatingLabel';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import useChipListField from '@lib/hooks/useChipListField';
import styles from './AssetCategoryChipListField.module.scss';

type Props = {
  fieldName?: string;
  values: Values;
  ids?: string[];
  disabled?: boolean;
};

const AssetCategoryChipListField = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<unknown>) => {
    const { fieldName = '', values, ids, disabled } = props;
    const { t } = useTranslation();
    const [paginationParams, setPaginationParams] = useState<
      PaginationParams & OrderingParams
    >({
      ...PAGINATION_PARAMS,
      [GetUrlParams.OrderingField]: 'name',
      [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
    });

    const page = _get(paginationParams, GetUrlParams.Page);
    const perPage = _get(paginationParams, GetUrlParams.PerPage);
    const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
    const orderingDirection = _get(
      paginationParams,
      GetUrlParams.OrderingDirection,
    );

    const queryVariables: AssetCategoriesSearchListQueryVariables = {
      page: Number(page),
      limit: Number(perPage),
    };

    const filters: AssetCategoryFilterInputObject[] = [];
    if (ids?.length) {
      filters.push({
        id: {
          grouping: FilterGroupingEnum.Or,
          predicate: {
            in: ids,
          },
        },
      });
    }
    if (filters.length) {
      queryVariables.filters = filters;
    }
    if (orderingField && orderingDirection) {
      queryVariables.sorts = {
        [orderingField]: orderingDirection,
      };
    }

    const query = useAssetCategoriesSearchListQuery({
      fetchPolicy: 'no-cache',
      variables: queryVariables,
      skip: !ids?.length,
    });

    const newItems = _get(
      query.data || query.previousData,
      'data.collection',
      [],
    ) as AssetCategory[];

    const {
      visibleItems,
      newFormFieldValue,
      handleRemove,
      handleLoadMore,
      currentPage,
      totalPages,
      firstLoading,
      error,
    } = useChipListField<AssetCategory>({
      fieldName,
      ids,
      values,
      paginationParams,
      setPaginationParams,
      queryResult: query,
      newItems,
    });

    if (error || firstLoading) return null;

    const newFormFieldValueRender = newFormFieldValue?.length
      ? newFormFieldValue.map((formFieldItem: SelectOption) => (
          <Chip
            label={formFieldItem.label}
            type={colorToChipType(_get(formFieldItem, 'color'))}
            leftIcon="circle"
            key={formFieldItem.value as string}
            rightIconComponent={
              disabled ? undefined : (
                <MaterialIcon
                  icon="cancel"
                  onClick={() => handleRemove(formFieldItem.value as string)}
                />
              )
            }
          />
        ))
      : null;

    const visibleItemsRender = visibleItems?.length
      ? visibleItems.map((visibleItem) => (
          <Chip
            label={_get(visibleItem, 'name')}
            type={colorToChipType(_get(visibleItem, 'color'))}
            leftIcon="circle"
            key={visibleItem.id}
            rightIconComponent={
              disabled ? undefined : (
                <MaterialIcon
                  icon="cancel"
                  onClick={() => handleRemove(visibleItem.id)}
                />
              )
            }
          />
        ))
      : null;

    if (newFormFieldValueRender === null && visibleItemsRender === null)
      return null;

    return (
      <div>
        <div className={styles.chipsList}>
          {newFormFieldValueRender}
          {visibleItemsRender}
        </div>
        {currentPage < totalPages && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={handleLoadMore}
            data-test-id="asset-category-chip-list-field__show-more"
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
    );
  },
);

export default memo(AssetCategoryChipListField);
